/** @jsx jsx */
import _ from 'lodash'
import React, { useMemo } from 'react'
import { config, useSpring } from 'react-spring'
import { Box, Flex, jsx } from 'theme-ui'
import { FormattedCurrency, TextOnTable, Txt } from '../../components'
import { PokerChipStacks, PositionedPokerChipStacks } from '../../components/PokerChipStack'
import {
  ClientGameHandController,
  IClientUserData,
} from '../../controllers/ClientGameHandController'
import { IPositioningMeasurements, IPositionProps, IUserPositioning } from './positionProps'

interface IPokerChipPotProps extends IPositionProps {
  controller: ClientGameHandController
  previousController?: ClientGameHandController
}

const FinalPokerChipPotWinner: React.SFC<{
  userData: IClientUserData
  amountWon: number
  measurements: IPositioningMeasurements
}> = ({ userData, amountWon, measurements }) => {
  // @ts-ignore
  const { left, top, opacity } = useSpring({
    from: {
      top: measurements.centerOfTable.top - 40,
      left: measurements.centerOfTable.left,
      opacity: 0,
    },
    to: [{ opacity: 1 }, { ...userData.position, opacity: 0 }],
    config: config.slow,
  })
  return (
    <PositionedPokerChipStacks
      style={{ opacity }}
      amount={amountWon}
      coords={{ left, top }}
      without={measurements.isLandscapeMode ? undefined : 'stacks'}
    />
  )
}

export const InGamePokerChipPots: React.SFC<IPokerChipPotProps> = ({
  controller,
  measurements,
}) => {
  return (
    <Flex
      mb={3}
      sx={{
        alignItems: 'center',
        justifyContent: 'center',
        opacity: controller.gameHand.finalResult ? 0 : 1,
        transition: 'opacity .3s linear',
      }}
    >
      {_.compact(controller.pots).map((pot, index) => (
        <TextOnTable sx={{ fontSize: [1, 2, 3], ml: index ? 5 : 0 }} key={index}>
          <Box sx={{ textAlign: 'left' }}>
            <Txt sx={{ lineHeight: 1 }}>
              <FormattedCurrency amount={pot} />
            </Txt>
            <Txt sx={{ fontSize: 0, opacity: 0.7, lineHeight: 1.1 }}>
              {controller.pots.length > 1 ? `Pot #${index + 1}` : 'Current Pot'}
            </Txt>
          </Box>
          <Box css={{ height: 30 }} my={3}>
            {!measurements.isLandscapeMode ? null : (
              <PokerChipStacks without={'sum'} amount={pot} />
            )}
          </Box>
        </TextOnTable>
      ))}
    </Flex>
  )
}

export const FinalPokerChipPots: React.FC<IPokerChipPotProps> = ({ controller, measurements }) => {
  const winningAmounts = useMemo(() => {
    const winningSolvedHands = controller.gameHand.finalResult?.automaticallyVisibleUserHands
      .filter((value) => value.amountWon > 0)
      .map(({ amountWon, userId }) => ({
        userId,
        amountWon,
      }))
    if (!winningSolvedHands) {
      return null
    }
    return _.compact([
      controller.gameHand.finalResult!.handEndedPrematurelyInfo,
      ...winningSolvedHands,
    ]).map(({ amountWon, userId }) => ({
      amountWon,
      userId,
    }))
  }, [controller.gameHand.finalResult])
  return (
    <React.Fragment>
      {winningAmounts &&
        winningAmounts.map(({ amountWon, userId }) => (
          <FinalPokerChipPotWinner
            userData={controller.getUserData(userId)}
            amountWon={amountWon}
            measurements={measurements}
            key={userId}
          />
        ))}
    </React.Fragment>
  )
}
